import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {translateConnectionObjectToArray} from 'nutshell-core';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetSmsEditions as GetSmsEditionsQuery,
    GetSmsEditionsVariables as GetSmsEditionsQueryVariables,
    SmsEditionFragment as SmsEdition,
} from 'nutshell-graphql-types';

import GET_SMS_EDITIONS from '../../graphql-wrappers/graphql/queries/get-sms-editions.graphql';
import {convertSmsEngagementStatsToGeneralStatObject} from '../../utils/helpers';

import {AudienceOutreachList} from './audience-outreach-list';

const LIMIT = 10;

type OwnProps = {
    audienceId: string;
};

export const AudienceOutreachSms = (props: OwnProps) => {
    const {
        data,
        error,
        loading: isLoading,
        fetchMore,
    } = useQuery<GetSmsEditionsQuery, GetSmsEditionsQueryVariables>(GET_SMS_EDITIONS, {
        variables: {
            first: LIMIT,
            audienceId: props.audienceId,
            statuses: ['SENT', 'SENDING', 'SCHEDULED', 'HALTED', 'IN_REVIEW'],
            campaignTypes: ['BROADCAST'],
        },
        notifyOnNetworkStatusChange: true,
    });

    const editions = data ? data.smsEditions : null;
    const recentEditions = editions ? translateConnectionObjectToArray(editions) : [];
    const formattedEditions = formatEditions(recentEditions);

    const fetchMoreRecentEditions = () => {
        return fetchMore({
            variables: {
                audienceId: props.audienceId,
                first: LIMIT,
                after: editions ? editions.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetSmsEditionsQuery,
                newResults: {
                    fetchMoreResult: GetSmsEditionsQuery;
                }
            ) => {
                const oldLatestOutreach = prev.smsEditions;
                const fetchedLatestOutreach = newResults.fetchMoreResult.smsEditions;

                const newLatestOutreach = {
                    ...oldLatestOutreach,
                    pageInfo: fetchedLatestOutreach.pageInfo,
                    edges: oldLatestOutreach.edges.concat(fetchedLatestOutreach.edges),
                } as const;

                return {smsEditions: newLatestOutreach};
            },
        });
    };

    return (
        <AudienceOutreachList
            editions={formattedEditions}
            // @ts-expect-error
            errorMessage={safelyGetGraphQLErrorMessage(error)}
            isLoading={isLoading}
            hasNextPage={editions ? editions.pageInfo.hasNextPage : false}
            onFetchMoreRows={fetchMoreRecentEditions}
            convertEngagementStats={convertSmsEngagementStatsToGeneralStatObject}
            outreachType='SMS'
        />
    );
};

const formatEditions = (publishedEditions?: SmsEdition[] | null) => {
    // @ts-expect-error
    let formattedEditions = [];
    if (publishedEditions) {
        formattedEditions = publishedEditions.map((publishedEdition) => {
            const {name: _name, ...restCampaign} = publishedEdition.campaign;
            const {from: _from, ...restEdition} = publishedEdition;
            const edition = {
                ...restEdition,
                campaign: restCampaign,
            } as const;

            return edition;
        });
    }

    // @ts-expect-error
    return formattedEditions;
};
